import axios from "axios";
import { axiosInstance } from "contexts/axiosApiInstance";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import {
  AddMembersRequest,
  CreateTeamRequest,
  DissociateModelRequest,
  EditTeamRequest,
  LinkModelRequest,
  RemoveMemberRequest,
  TeamAccess,
  TeamDetails,
  UpdateMemberAccessLevel,
  UserAccess,
  UserIdentification,
} from "target_wasm";

export interface TeamMember {
  user: UserIdentification;
  accessLevel: string;
  isCurrentUser: boolean;
  isPending: boolean;
}

// Sort: current user first, admins next, pending last.
const sortMembers = (members: TeamMember[]): TeamMember[] => {
  const currentUserId = String(localStorage.getItem("clientID"));
  return members.slice().sort((a, b) => {
    if (a.user.user_id_js() === currentUserId) return -1;
    if (b.user.user_id_js() === currentUserId) return 1;
    if (a.isPending !== b.isPending) return a.isPending ? 1 : -1;
    if (a.accessLevel === b.accessLevel) return 0;
    if (a.accessLevel === "Admin") return -1;
    if (b.accessLevel === "Admin") return 1;
    return 0;
  });
};

const toTeamMember = (m: UserAccess): TeamMember => {
  const currentUserId = String(localStorage.getItem("clientID"));
  return {
    user: m.user_js(),
    accessLevel: m.permission_display_js(),
    isCurrentUser: m.user_js().user_id_js() === currentUserId,
    isPending: m.is_pending_js(),
  };
};

export interface FetchSortedMembersPage {
  members: TeamMember[];
  continuationToken: string | null;
}

interface FetchPageOptions {
  continuationToken?: string | null;
  limit?: number;
  search?: string;
}

export const fetchSortedMembersPage = async (
  teamId: string,
  options: FetchPageOptions = {},
): Promise<FetchSortedMembersPage> => {
  const { continuationToken, limit, search } = options;
  const params = new URLSearchParams();
  if (continuationToken) params.append("continuation_token", continuationToken);
  if (limit !== undefined) params.append("limit", String(limit));
  if (search) params.append("search", search);
  const queryString = params.toString();
  const url = `/action/team/${teamId}/members${queryString ? `?${queryString}` : ""}`;
  try {
    const response = await axiosInstance.get(url);
    const rawUsers = response.data?.users ?? [];
    const members = sortMembers(
      rawUsers.map((u: unknown) => toTeamMember(UserAccess.from_js(u))),
    );
    return {
      members,
      continuationToken: response.data?.continuation_token ?? null,
    };
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to fetch team members: ${error.response?.data?.message || error.message}`,
      );
    }
    throw new Error("Error fetching members for this team");
  }
};

// TEAM MANAGEMENT API

export const fetchTeams = async (): Promise<TeamAccess[]> => {
  try {
    const response = await axiosInstance.get("/action/get_teams");
    return response.data.map((team) => {
      return TeamAccess.from_js(team);
    });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to fetch teams: ${error.response?.data?.message || error.message}`,
      );
    }
    throw new Error("Failed to fetch teams");
  }
};

export const createTeam = async (
  teamData?: CreateTeamRequest,
): Promise<TeamAccess> => {
  try {
    // D33: a team belongs to a company org, so the request has to name one.
    // Read outside React — this is a plain module, and the nav item that
    // reaches here is already hidden unless a non-personal org is active.
    const activeOrgId = useOrgContextStore.getState().activeOrgId;
    // No payload → server fills random name/color/asset.
    const payload = teamData
      ? teamData.to_json()
      : CreateTeamRequest.new_js(
          new TeamDetails("", "", "", "", BigInt(Date.now())),
          "[]",
          activeOrgId ?? undefined,
        ).to_json();
    const response = await axiosInstance.post("/action/create_team", payload);
    return TeamAccess.from_js(response.data);
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;
      if (status === 409) {
        throw new Error("A team with this name already exists.");
      } else if (status === 400 || status === 411) {
        throw new Error(serverMsg || "Invalid team details provided.");
      }
      throw new Error(serverMsg || "Failed to create team. Please try again.");
    }
    throw new Error("Failed to create team. Please try again.");
  }
};

export const deleteTeam = async (teamId: string): Promise<void> => {
  try {
    await axiosInstance.delete(`/action/delete_team/${teamId}`);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to delete team: ${error.response?.data?.message || error.message}`,
      );
    }
    throw new Error("Failed to delete team!");
  }
};

export const leaveTeam = async (teamId: string): Promise<void> => {
  try {
    const response = await axiosInstance.post(`/action/leave_team/${teamId}`);

    if (response.status !== 200) {
      throw new Error(`Failed to leave team: ${response.statusText}`);
    }
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 409) {
      throw new Error("You cannot leave the team as you are the last admin.");
    }
  }
};

// `GET /action/leave_team/{team_id}/preview` — fail-closed returns 503.
export interface LeaveTeamPreviewTable {
  table_id: string;
  table_name: string;
}

export interface LeaveTeamPreviewPerModel {
  model_id: string;
  model_name: string;
  model_color: string | null;
  model_icon_index: number | null;
  tables: LeaveTeamPreviewTable[];
}

export interface LeaveTeamPreview {
  status: "computed";
  models_affected: number;
  tables_affected: number;
  per_model: LeaveTeamPreviewPerModel[];
}

export class PreviewUnavailableError extends Error {
  constructor(public readonly reason: string = "unknown") {
    super(`Leave-team preview unavailable: ${reason}`);
    this.name = "PreviewUnavailableError";
  }
}

export const leaveTeamPreview = async (
  teamId: string,
): Promise<LeaveTeamPreview> => {
  try {
    const response = await axiosInstance.get<LeaveTeamPreview>(
      `/action/leave_team/${teamId}/preview`,
    );
    return response.data;
  } catch (error: unknown) {
    if (axios.isAxiosError(error) && error.response?.status === 503) {
      const reason: string = error.response?.data?.reason ?? "unknown";
      throw new PreviewUnavailableError(reason);
    }
    throw error;
  }
};

export const editTeam = async (
  request: EditTeamRequest,
): Promise<TeamAccess> => {
  try {
    const response = await axiosInstance.post(
      "/action/edit_team",
      request.to_json(),
    );
    return TeamAccess.from_js(response.data);
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      const status = error.response?.status;
      const serverMsg = error.response?.data?.message;
      if (status === 409) {
        throw new Error("A team with this name already exists.");
      } else if (status === 411) {
        throw new Error(
          serverMsg || "Team name must be at least 3 characters.",
        );
      } else if (status === 400) {
        throw new Error(serverMsg || "Team name cannot be empty.");
      }
      throw new Error(serverMsg || "Failed to update team. Please try again.");
    }
    throw new Error("Failed to update team. Please try again.");
  }
};

// MEMBER MANAGEMENT API (legacy, non-paginated)

export const fetchTeamMembers = async (
  teamId: string,
): Promise<UserAccess[]> => {
  try {
    const response = await axiosInstance.get(
      `/action/get_team_members/${teamId}`,
    );
    return response.data.map((userAccess) => {
      return UserAccess.from_js(userAccess);
    });
  } catch (error) {
    if (axios.isAxiosError(error)) {
      throw new Error(
        `Failed to fetch team members: ${error.response?.data?.message || error.message}`,
      );
    }
    throw new Error("Error fetching members for this team");
  }
};

/// Resolves with the addresses that could not be invited because the account
/// cap is full. The members that did resolve were still added, so this is a
/// warning on a success — the server used to drop them silently.
export const addTeamMembers = async (
  teamId: string,
  membersJson: string,
  teamName: string,
): Promise<{ atCapacityEmails: string[] }> => {
  try {
    const request = AddMembersRequest.new_js(teamId, membersJson, teamName);
    const { data } = await axiosInstance.post<{
      at_capacity_emails?: string[];
    }>("/action/add_team_members", request.to_json());
    return { atCapacityEmails: data?.at_capacity_emails ?? [] };
  } catch (error: unknown) {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409) {
        throw new Error("Some members are already in this team.");
      } else if (error.response?.status === 400) {
        throw new Error(
          error.response.data?.message || "Invalid email address provided.",
        );
      }
      throw new Error(
        error.response?.data?.message || "Failed to add members.",
      );
    }
    throw new Error("Failed to add members.");
  }
};

export const removeTeamMember = async (
  teamId: string,
  memberId: string,
  teamName: string,
  memberName: string,
  memberEmail: string,
): Promise<void> => {
  try {
    const request = RemoveMemberRequest.new_js(
      teamId,
      memberId,
      teamName,
      memberEmail,
      memberName,
    );
    await axiosInstance.post("/action/remove_team_member", request.to_json());
  } catch (error: unknown) {
    throw new Error("Failed to remove member from team");
  }
};

export const updateMemberAccessLevel = async (
  teamId: string,
  memberId: string,
  accessLevel: string,
  teamName: string,
  memberName: string,
  memberEmail: string,
): Promise<void> => {
  try {
    const request = UpdateMemberAccessLevel.new_js(
      teamId,
      memberId,
      accessLevel,
      teamName,
      memberName,
      memberEmail,
    );
    await axiosInstance.post("/action/update_team_member", request.to_json());
  } catch (error) {
    throw new Error("Failed to update team member access level");
  }
};

// MODEL MANAGEMENT API

export const linkModelToTeam = async (
  teamId: string,
  modelId: string,
  teamAccess: string,
): Promise<string[]> => {
  const request = LinkModelRequest.new_js(teamId, modelId, teamAccess);
  const response = await axiosInstance.post(
    "/action/link_model_to_team",
    request.to_json(),
  );
  return response.data;
};

export const dissociateModelFromTeam = async (
  teamId: string,
  modelId: string,
): Promise<string[]> => {
  const request = DissociateModelRequest.new_js(teamId, modelId);
  const response = await axiosInstance.post(
    "/action/dissociate_model",
    request.to_json(),
  );
  return response.data;
};

export const updateModelTeamAccess = async (
  teamId: string,
  modelId: string,
  teamAccess: string,
): Promise<string[]> => {
  try {
    const request = LinkModelRequest.new_js(teamId, modelId, teamAccess);
    const response = await axiosInstance.post(
      "/action/update_team_model",
      request.to_json(),
    );
    return response.data;
  } catch (error) {
    throw new Error("Failed to update model team access");
  }
};
